import { Button, Divider, Icon, Typography } from '@uigovpe/components';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { LabeledValue } from '@/shared/components/LabeledValue';
import {
  CertificateRequest,
  Option,
  Profile,
} from '@/shared/types/certificate-request';
import { formatDate, formatFullAddress, formatPhone } from '@/shared/utils/formatting';

interface ReviewStepProps {
  request: CertificateRequest;
  profile: Profile;
  incidentTypes: Option[];
  busy: boolean;
  error?: string | null;
  onEditIncident: () => void;
  onEditPersonalData: () => void;
  onEditContact: () => void;
  onBack: () => void;
  onSubmit: () => void;
}

interface BlockProps {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}

function ReviewBlock({ title, onEdit, children }: BlockProps) {
  return (
    <div className="cd-review__block">
      <div className="cd-review__block-header">
        <Typography variant="h3" size="default" fontWeight="bold">
          {title}
        </Typography>
        <Button
          label="Editar"
          icon={<Icon icon="edit_square" outline />}
          onClick={onEdit}
          outlined
        />
      </div>
      {children}
    </div>
  );
}

export function ReviewStep({
  request,
  profile,
  incidentTypes,
  busy,
  error,
  onEditIncident,
  onEditPersonalData,
  onEditContact,
  onBack,
  onSubmit,
}: ReviewStepProps) {
  const incidentTypeLabel =
    incidentTypes.find((type) => type.value === request.incidentType)?.label ??
    request.incidentType;

  return (
    <div className="cd-step-page">
      <section className="cd-step">
        <Typography variant="h2" size="lg" fontWeight="bold">
          Confira antes de enviar
        </Typography>
        <Typography variant="p" size="sm" className="cd-step__hint">
          Depois de enviar não é possível alterar. Se algo estiver errado, volte e
          corrija agora.
        </Typography>

        <ErrorNotice message={error} />

        <div className="cd-review">
          <ReviewBlock title="Ocorrência" onEdit={onEditIncident}>
            <LabeledValue label="Órgão" value="Corpo de Bombeiros Militar de Pernambuco" />
            <LabeledValue label="Natureza do fato" value={incidentTypeLabel} />
            <LabeledValue label="Data" value={formatDate(request.incidentDate)} />
            <LabeledValue label="Horário" value={request.incidentTime} />
            <LabeledValue label="Local" value={formatFullAddress(request.location)} />
            <LabeledValue label="Descrição" value={request.description} />
          </ReviewBlock>

          <Divider />

          <ReviewBlock title="Seus dados" onEdit={onEditPersonalData}>
            <LabeledValue label="Nome" value={profile.socialName || profile.name} />
            <LabeledValue label="CPF" value={profile.formattedCpf} />
            <LabeledValue label="Nome da mãe" value={profile.motherName} />
            <LabeledValue label="Nascimento" value={formatDate(profile.birthDate)} />
          </ReviewBlock>

          <Divider />

          <ReviewBlock title="Contato" onEdit={onEditContact}>
            <LabeledValue label="E-mail" value={profile.email} />
            <LabeledValue label="Telefone" value={formatPhone(profile.phone)} />
            <LabeledValue
              label="Situação"
              value={
                <span className="cd-review__confirmed">
                  <Icon icon="check_circle" /> E-mail confirmado
                </span>
              }
            />
          </ReviewBlock>
        </div>
      </section>

      <div className="cd-actions cd-actions--outside">
        <Button label="Voltar" outlined icon="pi pi-arrow-left" onClick={onBack} />
        <Button
          label="Enviar solicitação"
          icon="pi pi-send"
          loading={busy}
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
